import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

export default function Login() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { branding, loading, error } = useTenant();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Preencha e-mail e senha'); return; }
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(`/${tenantSlug}/dashboard`, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao fazer login';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-100">
      <div className="animate-spin h-10 w-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-screen bg-gray-100">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-700 mb-2">Agência não encontrada</h1>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  const bgStyle = branding?.imagemLogin
    ? { backgroundImage: `url(${branding.imagemLogin})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { backgroundColor: branding?.corPrimaria || 'var(--brand-primary)' };

  return (
    <div className="flex min-h-screen">
      {/* Left: branding panel */}
      <div className="hidden lg:flex flex-1 items-center justify-center" style={bgStyle}>
        {!branding?.imagemLogin && (
          <div className="text-center text-white px-8">
            {branding?.logo ? (
              <img src={branding.logo} alt="logo" className="h-20 mx-auto mb-6 object-contain" />
            ) : (
              <div className="h-20 w-20 mx-auto mb-6 bg-white/20 rounded-2xl flex items-center justify-center text-4xl font-bold">
                {(branding?.nomeDoSistema || 'T')[0]}
              </div>
            )}
            <h1 className="text-3xl font-bold mb-2">{branding?.nomeDoSistema || 'Turismo SaaS'}</h1>
            <p className="text-white/70 text-sm">{branding?.nomeEmpresa || ''}</p>
          </div>
        )}
      </div>

      {/* Right: form */}
      <div className="flex flex-1 items-center justify-center bg-white p-8">
        <div className="w-full max-w-sm">
          {branding?.logo && (
            <div className="text-center mb-6">
              <img src={branding.logo} alt="logo" className="h-14 mx-auto object-contain" />
            </div>
          )}

          {!branding?.logo && (
            <div className="lg:hidden text-center mb-8">
              <div className="h-12 w-12 mx-auto mb-3 rounded-xl flex items-center justify-center text-white text-xl font-bold"
                style={{ background: branding?.corPrimaria || 'var(--brand-primary)' }}>
                {(branding?.nomeDoSistema || 'T')[0]}
              </div>
              <h1 className="text-xl font-bold text-gray-800">{branding?.nomeDoSistema || 'Turismo SaaS'}</h1>
            </div>
          )}

          <h2 className="text-2xl font-bold text-gray-800 mb-1">Entrar</h2>
          <p className="text-gray-500 text-sm mb-8">Acesse sua conta para continuar</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent"
                placeholder="seu@email.com"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 text-white py-2.5 rounded-lg font-semibold text-sm transition-opacity disabled:opacity-60"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {submitting ? (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <><LogIn size={16} /> Entrar</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
